import { useTheme } from "@emotion/react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useAssetSentinelData } from "../AssetSentinel/useAssetSentinelData";
import { updateOrCreateUserSetting } from "../../reducers/UserSettingsReducer";
import { Box, Table, TableBody, TableCell, TableRow } from "@mui/material";
import PATDeleteButton from "../../components/Buttons/PATDeleteButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import { AMSModules, paginationStyle } from "../../utils/constants";
import { StatusBadge } from "../../utils/functions";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { getRowsPerPageOptions } from "../../utils/functions";
import { makeStyles } from "@mui/styles";
import EquipmentsTableToolbar from "./EquipmentsTableToolbar";
import PATDownButton from "../../components/Buttons/PATDownButton";
import PATUpButton from "../../components/Buttons/PATUpButton";
import AddEditEquipmentDialog from "./AddEditEquipmentDialog";
import EquipmentCheckInOutDialog from "./EquipmentCheckInOutDialog";
import QuickAuditDialog from "./QuickAuditDialog";
import QuickAssignDialog from "./QuickAssignDialog";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
	make: [],
	model: [],
	year: [],
	department: [],
	employee: [],
	funding: [],
};

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const Equipments = () => {
	const classes = useStyles();

	const history = useHistory();
	const dispatch = useDispatch();
	const theme = useTheme();
	const { equipment, vehicles, codes, employees } = useAssetSentinelData();
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData, isAuthenticated } = useSelector((state) => state.user);
	const { userSettings } = useSelector((state) => state.userSettings);
	const themeMode = useSelector((state) => state.theme.mode);
	const filtersInfo = useSelector((state) => state.filtersInfo);
	const [openDialog, setOpenDialog] = useState(false);
	const [openCheckDialog, setOpenCheckDialog] = useState(false);
	const [selectedEquipment, setSelectedEquipment] = useState(null);
	const [openAuditDialog, setOpenAuditDialog] = useState(false);
	const [quickAssignOpen, setQuickAssignOpen] = useState(false);

	const total = equipment?.length || 0;
	const loading = false;

	const [search, setSearch] = useState("");
	const [columnsOrder, setColumnsOrder] = useState([]);
	const [columnsSize, setColumnsSize] = useState([]);
	const [settingsLoaded, setSettingsLoaded] = useState(false);
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
	const [page, setPage] = useState(INITIAL_PAGE);
	const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
	const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
	const [filters, setFilters] = useState(
		filtersInfo?.equipments || initialFilterState,
	);

	const currentAgency = useRef(null);

	const baseColumns = useMemo(
		() => [
			{
				field: "type",
				headerName: "Type",
				width: 100,
				sortable: true,
			},
			{
				field: "propertyNumber",
				headerName: "Property#",
				width: 150,
				sortable: true,
			},
			{
				field: "serialNumber",
				headerName: "Serial#",
				width: 150,
				sortable: true,
			},
			{
				field: "brand/model",
				headerName: "Brand / Model",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return `${row?.brand} ${row.model}`;
				},
			},
			{
				field: "condition",
				headerName: "Condition",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.condition} />;
				},
			},
			{
				field: "status",
				headerName: "Status",
				width: 150,
				sortable: true,
				renderCell: ({ row }) => {
					return <StatusBadge status={row?.status} />;
				},
			},
			{
				field: "location",
				headerName: "Location",
				width: 150,
				sortable: true,
			},
			{
				field: "assignedVehicleID",
				headerName: "Vehicle",
				width: 100,
				sortable: true,
			},
			{
				field: "assignedOfficerID",
				headerName: "Employee",
				width: 100,
				sortable: true,
			},
			{
				field: "serviceDue",
				headerName: "Service Due",
				width: 100,
				sortable: true,
			},
			{
				field: "actions",
				headerName: "Actions",
				type: "actions",
				width: 150,
				align: "center",
				sortable: false,
				renderCell: ({ row }) => {
					const eqStatuses = codes?.equipmentStatuses || [];
					const curStatusDef = eqStatuses.find(
						(s) => s.value === row?.status,
					);
					const isOut = curStatusDef?.isCheckedOut === true;
					return row.id ? (
						<Table>
							<TableBody>
								<TableRow>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										{isOut ? (
											<PATDownButton
												tooltipText="Check In"
												onClick={() => {
													setSelectedEquipment(row);
													setOpenCheckDialog(true);
												}}
												size="20px"
											/>
										) : (
											<PATUpButton
												tooltipText="Check Out"
												onClick={() => {
													setSelectedEquipment(row);
													setOpenCheckDialog(true);
												}}
												size="20px"
											/>
										)}
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATEditButton
											tooltipText="Edit Equipment"
											// onClick={() =>
											// 	history.push(
											// 		`${PATH_APPLICANTS_EDIT}${row.id}`,
											// 	)
											// }
											size="20px"
										/>
									</TableCell>
									<TableCell
										align="right"
										sx={{ padding: 0.5 }}
									>
										<PATDeleteButton
											tooltipText="Delete Equipment"
											// onClick={() => {
											// 	setSelectedId(row.id);
											// 	setOpenDeleteDialog(true);
											// }}
											size="20px"
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					) : null;
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[history],
	);

	const columns = useMemo(() => {
		const hasOrder = columnsOrder.length > 0;
		const hasSizes = Object.keys(columnsSize).length > 0;

		if (!hasOrder && !hasSizes) return baseColumns;

		let baseOrderedColumns;

		if (hasOrder) {
			const ordered = columnsOrder
				.map((field) => baseColumns.find((col) => col.field === field))
				.filter(Boolean);

			const remaining = baseColumns.filter(
				(col) => !columnsOrder.includes(col.field),
			);

			const actionsIndex = ordered.findIndex(
				(col) => col.field === "actions",
			);
			const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

			baseOrderedColumns = [
				...ordered.slice(0, insertAt),
				...remaining,
				...ordered.slice(insertAt),
			];
		} else {
			baseOrderedColumns = baseColumns;
		}

		const finalColumns = baseOrderedColumns.map((col) => ({
			...col,
			width: columnsSize[col.field] ?? col.width ?? 100,
		}));

		return finalColumns;
	}, [baseColumns, columnsOrder, columnsSize]);

	const allColumnFields = baseColumns.map((col) => col.field);

	useEffect(() => {
		const defaultVisibility = {};
		currentAgency.current = userAgency;
		allColumnFields.forEach((field) => {
			defaultVisibility[field] = true;
		});
		setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isAuthenticated || settingsLoaded) return;

		const initialize = async () => {
			const settingsMap = Object.fromEntries(
				userSettings.map((s) => [s.key, s.value]),
			);
			if (settingsMap["equipmentsTableColumnOrder"]) {
				setColumnsOrder(
					JSON.parse(settingsMap["equipmentsTableColumnOrder"]),
				);
			}

			if (settingsMap["equipmentsTableColumnSizes"]) {
				const widthArray = JSON.parse(
					settingsMap["equipmentsTableColumnSizes"],
				);
				const result = widthArray.reduce((acc, curr) => {
					acc[curr.field] = curr.width;
					return acc;
				}, {});
				setColumnsSize(result);
			}

			if (settingsMap["equipmentsTableColumnVisibility"]) {
				setColumnVisibilityModel(
					JSON.parse(settingsMap["equipmentsTableColumnVisibility"]),
				);
			}

			if (settingsMap["equipmentsTableSortModel"]) {
				const savedSortModel = JSON.parse(
					settingsMap["equipmentsTableSortModel"],
				);
				if (Array.isArray(savedSortModel)) {
					const validSortModel = savedSortModel.filter((model) =>
						baseColumns.some((col) => col.field === model.field),
					);
					if (validSortModel.length > 0) {
						setSortModel(savedSortModel);
					}
				}
			}
			setSettingsLoaded(true);
		};

		initialize();
	}, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

	const handleSortModelChange = useCallback(
		(model) => {
			const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

			// Avoid unnecessary state updates if sort model didn't change
			const isSortModelDifferent =
				JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

			if (isSortModelDifferent) {
				setSortModel(newSortModel);
				setPage(INITIAL_PAGE);

				if (userData?.user?.ptsUserID) {
					dispatch(
						updateOrCreateUserSetting(
							userData.user.ptsUserID,
							"equipmentsTableSortModel",
							JSON.stringify(newSortModel),
						),
					);
				}
			}
		},
		[sortModel, dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnOrderChange = useCallback(
		(params, event, details) => {
			const newColumnOrder = details.api
				.getAllColumns()
				.map((col) => col.field);
			if (userData?.user?.ptsUserID) {
				dispatch();
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"equipmentsTableColumnOrder",
					JSON.stringify(newColumnOrder),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const handleColumnWidthChange = (params, event, details) => {
		const columnSizes = details.api.getAllColumns().map((col) => {
			return {
				field: col.field,
				width: col.width,
			};
		});
		if (userData?.user?.ptsUserID) {
			dispatch(
				updateOrCreateUserSetting(
					userData.user.ptsUserID,
					"equipmentsTableColumnSizes",
					JSON.stringify(columnSizes),
				),
			);
		}
	};

	const handleColumnVisibilityModelChange = useCallback(
		(newModel) => {
			setColumnVisibilityModel(newModel);
			if (userData?.user?.ptsUserID) {
				dispatch(
					updateOrCreateUserSetting(
						userData.user.ptsUserID,
						"equipmentsTableColumnVisibility",
						JSON.stringify(newModel),
					),
				);
			}
		},
		[dispatch, userData?.user?.ptsUserID],
	);

	const rowsPerPageOptions = useMemo(
		() => getRowsPerPageOptions(total),
		[total],
	);

	const handleOnFilterChange = (newFilters) => {
		setFilters(newFilters);
		dispatch({ type: AMSModules.Equipments, payload: newFilters });
	};

	const resetFilters = () => {
		setFilters(initialFilterState);
		dispatch({ type: AMSModules.Equipments, payload: initialFilterState });
	};

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
					marginTop: theme.spacing(0.5),
				}}
			>
				<EquipmentsTableToolbar
					openAdd={() => setOpenDialog(true)}
					search={search}
					setSearch={setSearch}
					onAuditClick={() => setOpenAuditDialog(true)}
					onAssignClick={() => setQuickAssignOpen(true)}
					// status={status}
					// vehicles={vehicles}
					// setStatus={setStatus}
					// codes={codes}
					// employees={employees}
					// fundingSources={fundingSources}
					// serviceFilter={serviceFilter}
					// setServiceFilter={setServiceFilter}
					// assignmentFilter={assignmentFilter}
					// setAssignmentFilter={setAssignmentFilter}
					resetFilters={resetFilters}
					filters={filters}
					onFilterChange={handleOnFilterChange}
				/>

				<Box
					sx={{
						flex: 1,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<DataGridPro
						rows={equipment}
						columns={columns}
						initialState={{ pinnedColumns: { right: ["actions"] } }}
						// getRowId={(row) => row.GUID}
						getRowId={(row) => row.id}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={
							handleColumnVisibilityModelChange
						}
						pageSize={pageSize}
						rowsPerPageOptions={rowsPerPageOptions}
						pagination
						paginationMode="server"
						sortModel={sortModel}
						sortingMode="server"
						onSortModelChange={handleSortModelChange}
						rowCount={total}
						page={page}
						onPageChange={setPage}
						onPageSizeChange={setPageSize}
						loading={loading}
						disableSelectionOnClick
						onColumnOrderChange={handleColumnOrderChange}
						onColumnWidthChange={handleColumnWidthChange}
						sx={{
							...paginationStyle(theme, themeMode),
							color: theme.palette.colorFont,
							margin: 1,
						}}
					/>
				</Box>
			</Box>
			<AddEditEquipmentDialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSave={() => undefined}
				// Options
				statusOptions={[]}
				typeOptions={[]}
				categoryCodeOptions={[]}
				conditionOptions={[]}
				divisionOptions={[]}
				employeeOptions={[]}
				vehicleOptions={[]}
				fundingSourceOptions={[]}
				serviceRequirements={[]}
				// Assignment history
				assignmentHistory={[]}
				// Maintenance data
				maintenanceRecords={[]}
				onAddMaintenance={() => undefined}
				onEditMaintenance={() => undefined}
				canManageMaintenance={true}
			/>
			<EquipmentCheckInOutDialog
				open={openCheckDialog}
				onClose={() => setOpenCheckDialog(false)}
				onSave={() => undefined}
				initialData={selectedEquipment}
				employeeOptions={employees
					.filter((e) => e.isActive)
					.map((e) => ({
						value: e.id,
						label: `${e.badge} — ${e.rank} ${e.lastName}, ${e.firstName}`,
					}))}
				vehicleOptions={vehicles
					.filter((v) => v.status !== "Retired")
					.map((v) => ({
						value: v.id,
						label: `${v.unitNumber} — ${v.year} ${v.make} ${v.model}`,
					}))}
				statusOptions={
					codes?.equipmentStatuses?.filter(
						(s) => s.isActive !== false,
					) || []
				}
			/>
			<QuickAuditDialog
				open={openAuditDialog}
				onClose={() => setOpenAuditDialog(false)}
				onSave={() => undefined}
				equipment={equipment}
				vehicles={vehicles}
				employees={employees}
				// defaultAuditor={user?.fullName}
			/>

			<QuickAssignDialog
				open={quickAssignOpen}
				onClose={() => setQuickAssignOpen(false)}
				onSave={() => undefined}
				equipment={equipment}
				vehicles={vehicles}
				employees={employees}
				codes={codes}
			/>
		</>
	);
};

export default Equipments;
